// Shared helpers for the title colour swatch feature — used by the
// collection grid (collection/instant-search.ts) and the product page
// (product/title-swatches.ts). Siblings are separate Shopify products that
// are colourways of the same watch, grouped by the custom.title_internal
// metafield (staff-only), falling back to the visible title when blank.

/** Normalise a product title for grouping siblings that share the same name */
export const normalizeTitleKey = (title: unknown) =>
  String(title || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The internal title (custom.title_internal metafield, staff-only) that marks
 * products as colourways of the same watch, falling back to the visible title
 * when the metafield is blank. Products that fall back only ever group with
 * other fallback products — a blank product never joins a curated group.
 * NOTE: the metafield must be indexed by the Algolia Shopify app (as a facet)
 * to be present on records as meta.custom.title_internal.
 */
export const getInternalTitle = (hit: any): string =>
  String(hit.meta?.custom?.title_internal || '').trim();

/** Grouping key for a hit: internal title when set, else the visible title */
export const getGroupTitle = (hit: any): string =>
  getInternalTitle(hit) || String(hit.title || '').trim();

/**
 * Cache/group key for a hit — prefixed so a curated internal-title group can
 * never collide with a visible-title fallback group of the same wording
 */
export const getGroupKey = (hit: any): string =>
  (getInternalTitle(hit) ? 'm:' : 't:') + normalizeTitleKey(getGroupTitle(hit));

/**
 * Title colour swatches only apply to watches. Checks the Shopify product
 * type, falling back to the title when the index has no product_type.
 */
export const isWatchHit = (hit: any) =>
  hit.product_type
    ? /timepiece|watch/i.test(String(hit.product_type))
    : /\bwatch\b/i.test(String(hit.title || ''));

/**
 * Whether a hit gets title swatches: any product with a curated internal
 * title (e.g. Montblanc pens/leather grouped by model line), plus watches via
 * the visible-title fallback. Non-watches never group on visible title alone.
 */
export const isSwatchHit = (hit: any) =>
  !!getInternalTitle(hit) || isWatchHit(hit);

/**
 * A sibling only earns a swatch if it has inventory. The available-online
 * tag (named_tags.available) is deliberately NOT required — in-store-only
 * watches still count as colourways; they just can't be added to cart.
 */
export const isInStock = (hit: any) => !!hit.inventory_available;
